import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { EyeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ApiURL } from "../../../../services/apiConstants";
import { post } from "../../../../services/services";
import { MOCK_MAINTENANCE_LOG_TASKS, USE_MOCK_MAINTENANCE_LOG } from "./mockMaintenanceLog";

const { Option } = Select;
const { RangePicker } = DatePicker;

const STATUS_COLORS = {
  pending: "gold",
  in_progress: "blue",
  completed: "green",
  overdue: "red",
  cancelled: "default",
};

const TYPE_LABELS = {
  calendar: "Calendar",
  running_hours: "Running Hours",
};

const getArrayData = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.response_data)) return res.response_data;
  if (Array.isArray(res?.tasks)) return res.tasks;
  if (Array.isArray(res?.job_tasks)) return res.job_tasks;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "N/A";
};

const getTaskId = (record) =>
  record?.id || record?.task_id || record?.job_task_id || record?.job_register_task_id;

const getTaskFallback = () => (USE_MOCK_MAINTENANCE_LOG ? MOCK_MAINTENANCE_LOG_TASKS : []);

const getShipFallback = () => {
  if (!USE_MOCK_MAINTENANCE_LOG) return [];
  const shipMap = new Map();
  MOCK_MAINTENANCE_LOG_TASKS.forEach((task) => {
    if (task.ship_id && task.ship_name) {
      shipMap.set(task.ship_id, { id: task.ship_id, vessel_name: task.ship_name });
    }
  });
  return Array.from(shipMap.values());
};

const JobTasksList = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [ships, setShips] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({
    ship_id: "",
    status: "",
    task_type: "",
    due_range: null,
  });
  const [searchText, setSearchText] = useState("");

  const fetchShips = async () => {
    try {
      const res = await post(ApiURL.list_ships_view_com, new FormData());
      const apiShips = res?.status === true ? res?.ships || [] : [];
      setShips(apiShips.length > 0 ? apiShips : getShipFallback());
    } catch (error) {
      console.error("Error fetching ships:", error);
      setShips(getShipFallback());
    }
  };

  const fetchTasks = async (nextFilters = filters) => {
    setLoader(true);
    try {
      const formData = new FormData();
      const userData = JSON.parse(localStorage.getItem("user_data"));
      const companyId = userData?.data?.company?.company_id;

      if (companyId) formData.append("company_id", companyId);
      if (nextFilters.ship_id) formData.append("ship_id", nextFilters.ship_id);
      if (nextFilters.status) formData.append("status", nextFilters.status);
      if (nextFilters.task_type) formData.append("task_type", nextFilters.task_type);
      if (nextFilters.due_range?.[0]) {
        formData.append("due_date_from", nextFilters.due_range[0].format("YYYY-MM-DD"));
      }
      if (nextFilters.due_range?.[1]) {
        formData.append("due_date_to", nextFilters.due_range[1].format("YYYY-MM-DD"));
      }

      const res = await post(ApiURL.list_job_tasks, formData);
      const apiTasks = res?.status === true ? getArrayData(res) : [];
      setTasks(apiTasks.length > 0 ? apiTasks : getTaskFallback());
    } catch (error) {
      console.error("Error fetching job tasks:", error);
      setTasks(getTaskFallback());
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchShips();
    fetchTasks();
  }, []);

  const updateFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: key === "due_range" ? value : value || "" };
    setFilters(nextFilters);
    fetchTasks(nextFilters);
  };

  const filteredTasks = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return tasks.filter((task) => {
      const taskType = task.task_type || task.maintenance_type;
      const dueDate = task.due_date || task.next_due_date;

      if (filters.ship_id && String(task.ship_id) !== String(filters.ship_id)) return false;
      if (filters.status && String(task.status) !== String(filters.status)) return false;
      if (filters.task_type && String(taskType) !== String(filters.task_type)) return false;
      if (filters.due_range?.[0] && dueDate) {
        const due = dayjs(dueDate);
        if (due.isValid() && due.isBefore(filters.due_range[0], "day")) return false;
      }
      if (filters.due_range?.[1] && dueDate) {
        const due = dayjs(dueDate);
        if (due.isValid() && due.isAfter(filters.due_range[1], "day")) return false;
      }
      if (!query) return true;

      const searchable = [
        getTaskId(task),
        task.code,
        task.job_code,
        task.job_description,
        task.machinery_name,
        task.machinary_name,
        task.ship_name,
        task.priority,
        task.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [filters, searchText, tasks]);

  const columns = [
    {
      title: "Task ID",
      key: "id",
      align: "center",
      width: 90,
      render: (_, record) => getTaskId(record) || "N/A",
    },
    {
      title: "Code",
      key: "code",
      align: "center",
      render: (_, record) => record.code || record.job_code || "N/A",
    },
    {
      title: "Machinery",
      key: "machinery_name",
      render: (_, record) => record.machinery_name || record.machinary_name || "N/A",
    },
    {
      title: "Task",
      key: "job_description",
      render: (_, record) => record.job_description || record.description || "N/A",
    },
    {
      title: "Type",
      key: "task_type",
      align: "center",
      render: (_, record) => {
        const type = record.task_type || record.maintenance_type;
        return <Tag color={type === "running_hours" ? "purple" : "cyan"}>{TYPE_LABELS[type] || "N/A"}</Tag>;
      },
    },
    {
      title: "Due",
      key: "due",
      align: "center",
      render: (_, record) => {
        const type = record.task_type || record.maintenance_type;
        if (type === "running_hours") {
          return record.due_hours || record.next_due_hours || "N/A";
        }
        return formatDate(record.due_date || record.next_due_date);
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      align: "center",
      render: (priority) => <Tag color={priority === "High" ? "red" : priority === "Medium" ? "orange" : "green"}>{priority || "N/A"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status = "pending") => (
        <Tag color={STATUS_COLORS[String(status).toLowerCase()] || "default"}>
          {String(status).replaceAll("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 90,
      render: (_, record) => {
        const taskId = getTaskId(record);
        return (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            disabled={!taskId}
            onClick={() => navigate(`/maintenance-log/${taskId}`, { state: { taskData: record } })}
            style={{ backgroundColor: "#D91D3B", borderColor: "#D91D3B" }}
          />
        );
      },
    },
  ];

  return (
    <div className="white-container" style={{ padding: "10px" }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8} md={6}>
          <label className="block-label" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Select Ship
          </label>
          <Select
            placeholder="---- Select Ship ----"
            style={{ width: "100%" }}
            value={filters.ship_id}
            onChange={(value) => updateFilter("ship_id", value)}
            allowClear
          >
            <Option value="">All Ships</Option>
            {ships.map((ship) => (
              <Option key={ship.id} value={ship.id}>
                {ship.vessel_name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={8} md={5}>
          <label className="block-label" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Status
          </label>
          <Select
            placeholder="All Status"
            style={{ width: "100%" }}
            value={filters.status}
            onChange={(value) => updateFilter("status", value)}
            allowClear
          >
            <Option value="">All Status</Option>
            <Option value="pending">Pending</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="completed">Completed</Option>
            <Option value="overdue">Overdue</Option>
          </Select>
        </Col>
        <Col xs={24} sm={8} md={5}>
          <label className="block-label" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Task Type
          </label>
          <Select
            placeholder="All Types"
            style={{ width: "100%" }}
            value={filters.task_type}
            onChange={(value) => updateFilter("task_type", value)}
            allowClear
          >
            <Option value="">All Types</Option>
            <Option value="calendar">Calendar</Option>
            <Option value="running_hours">Running Hours</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <label className="block-label" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Due Date
          </label>
          <RangePicker
            style={{ width: "100%" }}
            value={filters.due_range}
            onChange={(value) => updateFilter("due_range", value)}
          />
        </Col>
      </Row>

      <div className="row" style={{ padding: "15px 10px", marginTop: 10 }}>
        <div className="col-6" style={{ alignSelf: "center" }}>
          <div className="theme-content-title text-left">
            <h3 style={{ margin: 0 }}>Maintenance Log</h3>
          </div>
        </div>
        <div className="col-6" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Space>
            <Input
              placeholder="Search tasks..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              style={{ width: 240 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchTasks()} loading={loader}>
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredTasks}
        loading={loader}
        rowKey={(record, index) => getTaskId(record) || `${record.code || "task"}-${record.due_date || record.due_hours || index}`}
        bordered
        pagination={{ pageSize: 10 }}
        className="custom-table"
        locale={{ emptyText: <Empty description="No job tasks found" /> }}
        style={{ marginTop: 10 }}
      />
    </div>
  );
};

export default JobTasksList;
